import { useEffect, useState } from "react";
import { toast } from "sonner";

import legalAdviceHeadset from "assets/svg/legal-advice-headset.svg";
import radioCheck from "assets/svg/radio-check.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  getOppositionEvidenceByConflictingProprietor,
  getOppositionProgress,
  recordOppositionCallClientForRejoinder,
  OppositionWorkflowStep,
  type OppositionEvidenceByCpItem,
  type OppositionProgressStep,
} from "api/opposition";

export const OPPOSITION_CALL_CLIENT_FOR_REJOINDER_FORM_ID =
  "opposition-call-client-for-rejoinder-form";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionCallClientForRejoinderView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [marks, setMarks] = useState<OppositionEvidenceByCpItem[]>([]);
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setMarks([]);
      setRecorded(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getOppositionEvidenceByConflictingProprietor(leadId),
      getOppositionProgress(leadId).catch(() => null),
    ])
      .then(([data, progress]) => {
        if (cancelled) return;
        // Eligible: marks where Conflicting Proprietor filed Evidence (YES).
        setMarks(
          data.conflictingMarks.filter(
            (m) => m.evidenceFiledByConflictingProprietor === true
          )
        );
        if (progress) {
          const step = progress.steps.find(
            (s: OppositionProgressStep) =>
              s.slug ===
              OppositionWorkflowStep.OP_S3_20_CallClientForRejoinderByOpponent
          );
          setRecorded(Boolean(step?.isCompleted || step?.isSkipped));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load step data");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || recording) return;
    if (recorded) {
      onSuccess?.(leadId);
      return;
    }
    if (marks.length === 0) {
      toast.error(
        "No eligible applications. Mark evidenceFiledByConflictingProprietor=YES in Step 17 first."
      );
      return;
    }
    setRecording(true);
    try {
      await recordOppositionCallClientForRejoinder(leadId, {
        conflictingMarkIds: marks.map((m) => m.id),
      });
      setRecorded(true);
      toast.success("Call Client for Rejoinder step recorded.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record step"
      );
    } finally {
      setRecording(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_CALL_CLIENT_FOR_REJOINDER_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {marks.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {marks.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] bg-[#222222] text-white"
              style={{ fontSize: "0.75rem" }}
            >
              <RadioCircle active />
              {formatTmNoForDisplay(m.applicationNo ?? "")}
            </div>
          ))}
        </div>
      )}

      <div
        className="flex flex-row items-center gap-4 p-4"
        style={{
          background: "#FFF8EE",
          border: "1.5px dashed #F5A623",
          borderRadius: "12px",
        }}
      >
        <img
          src={legalAdviceHeadset}
          alt=""
          className="w-[60px] h-[60px] flex-shrink-0"
        />
        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <p className="text-sm font-normal text-[#222222]" style={{ lineHeight: "20px" }}>
            Call the client and explain the need to file a Rejoinder Affidavit
            in response to the Conflicting Proprietor&apos;s Evidence.
          </p>
          {recorded ? (
            <span className="text-xs font-semibold text-[#222]">
              Call Completed
            </span>
          ) : (
            <button
              type="button"
              onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
              disabled={recording || marks.length === 0}
              className="self-start h-8 px-4 text-xs font-medium text-[#222222] bg-white border border-[#D1D5DB] rounded-md hover:bg-[#F9FAFB] disabled:opacity-60"
              style={{ lineHeight: "20px" }}
            >
              {recording ? "Recording…" : "Call Completed"}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
